import { useState, useEffect, useRef } from 'react';

const STATUS_CLS = {
  Active:    'bg-green-100 text-green-700',
  'Awaiting Payment': 'bg-teal-100 text-teal-700',
  Issued:    'bg-blue-100 text-blue-700',
  Draft:     'bg-gray-100 text-gray-500',
  Pipeline:  'bg-indigo-100 text-indigo-600',
  Won:       'bg-emerald-100 text-emerald-700',
  Lost:      'bg-red-100 text-red-500',
  Expired:   'bg-amber-100 text-amber-700',
  Completed: 'bg-purple-100 text-purple-600',
  Cancelled: 'bg-red-100 text-red-400',
  Archived:  'bg-gray-100 text-gray-400',
};

function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`text-[7px] px-1 py-0.5 rounded font-semibold flex-shrink-0 ${STATUS_CLS[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

function Row({ s, pick, isFav, onToggleFavourite }) {
  return (
    <div
      onMouseDown={e => { e.preventDefault(); pick(s.id); }}
      className="px-3 py-1.5 text-[9px] cursor-pointer hover:bg-gold/10 border-b border-gray-50 last:border-0 flex items-center gap-2"
    >
      {onToggleFavourite && (
        <button type="button" onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onToggleFavourite(s.id); }}
          title={isFav ? 'Remove from favourites' : 'Add to favourites'}
          className={`flex-shrink-0 leading-none ${isFav ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}>
          {isFav ? '★' : '☆'}
        </button>
      )}
      <StatusBadge status={s.status} />
      <span className="flex-1 min-w-0">
        <span className="font-semibold font-mono text-navy">{s.reference}</span>
        {s.detail && <span className="text-gray-600 ml-2">{s.detail.slice(0, 60)}</span>}
        {!s.detail && s.client_name && <span className="text-gray-500 ml-2">{s.client_name}</span>}
        {s.detail && s.client_name && <span className="text-gray-400 ml-1.5">({s.client_name})</span>}
      </span>
    </div>
  );
}

/**
 * Searchable, sorted SOW picker — replaces plain <select> elements wherever
 * SOWs are chosen in a form. Shows status badge alongside each SOW.
 *
 * Props:
 *   sows              — array of { id, reference, detail?, client_name?, status? }
 *   value             — selected sow id (string | number | '')
 *   onChange          — (id: string) => void  — called with '' to clear
 *   placeholder       — string  (default '— Select SOW —')
 *   disabled          — bool
 *   favouriteIds      — optional array of sow ids — when given, favourites are
 *                       pinned to the top and get a star toggle (most people
 *                       only work a couple of projects at a time)
 *   onToggleFavourite — (id: string) => void — required if favouriteIds is given
 */
export default function SowSelect({ sows = [], value, onChange, placeholder = '— Select SOW —', disabled, inputClassName, favouriteIds, onToggleFavourite }) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const favSet = favouriteIds ? new Set(favouriteIds.map(String)) : null;
  const isFav = (id) => !!favSet?.has(String(id));

  const sorted = [...sows].sort((a, b) => {
    if (favSet) {
      const fa = isFav(a.id), fb = isFav(b.id);
      if (fa !== fb) return fa ? -1 : 1;
    }
    return (a.reference || '').localeCompare(b.reference || '');
  });
  const selected = value ? sorted.find(s => String(s.id) === String(value)) : null;

  const filtered = search
    ? sorted.filter(s =>
        (s.reference   || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.detail      || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.client_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : sorted;
  // Split into favourites/rest for a labelled split only when browsing the
  // unfiltered list — once searching, just show matches in rank order.
  const favRows   = (!search && favSet) ? filtered.filter(s => isFav(s.id)) : [];
  const otherRows = (!search && favSet) ? filtered.filter(s => !isFav(s.id)) : filtered;

  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  function pick(id) {
    onChange(String(id));
    setOpen(false);
    setSearch('');
  }

  // Clearing drops straight back into an open, focused search box — removing a
  // project should feel like "pick a different one", not a dead end that needs
  // an extra click to reopen.
  function clear() {
    onChange('');
    setSearch('');
    setOpen(true);
  }

  useEffect(() => {
    if (open && !selected) inputRef.current?.focus();
  }, [open, selected]);

  if (selected) {
    return (
      <div className={`flex items-center gap-1.5 border border-gray-200 rounded px-2 py-1 bg-white text-[9px] ${disabled ? 'opacity-50' : ''}`}>
        <StatusBadge status={selected.status} />
        <span className="flex-1 truncate font-semibold text-navy">
          <span className="font-mono">{selected.reference}</span>
          {(selected.detail || selected.client_name) && (
            <span className="font-normal text-gray-500 ml-1.5">
              {selected.detail
                ? selected.detail.slice(0, 60)
                : selected.client_name}
            </span>
          )}
        </span>
        {onToggleFavourite && !disabled && (
          <button type="button" onClick={() => onToggleFavourite(selected.id)}
            title={isFav(selected.id) ? 'Remove from favourites' : 'Add to favourites'}
            className={`flex-shrink-0 leading-none rounded p-1 -m-1 transition-colors ${isFav(selected.id) ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}>
            {isFav(selected.id) ? '★' : '☆'}
          </button>
        )}
        {!disabled && (
          <button type="button" onClick={clear} title="Remove — pick a different project"
            className="text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0 leading-none rounded p-1 -m-1 transition-colors">✕</button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClassName ?? 'form-input text-[9px] w-full'}
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-0.5 border border-gray-200 rounded bg-white shadow-lg max-h-[260px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[9px] text-gray-400 italic">
              {search ? `No SOWs match "${search}"` : 'No projects available'}
            </div>
          ) : (
            <>
              {favRows.length > 0 && (
                <>
                  <div className="px-3 py-1 text-[7px] font-bold text-amber-600 uppercase tracking-wide bg-amber-50 sticky top-0">★ Favourites</div>
                  {favRows.map(s => (
                    <Row key={s.id} s={s} pick={pick} isFav onToggleFavourite={onToggleFavourite} />
                  ))}
                  <div className="px-3 py-1 text-[7px] font-bold text-gray-400 uppercase tracking-wide bg-gray-50 sticky top-0">All projects</div>
                </>
              )}
              {otherRows.map(s => (
                <Row key={s.id} s={s} pick={pick} isFav={isFav(s.id)} onToggleFavourite={onToggleFavourite} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
